/**
 * Main app shell with premium navigation
 * Apple-grade design: calm, quiet intelligence, reduced cognitive load
 * Clean hamburger menu control - no profile avatar, purely functional
 *
 * DESIGN SYSTEM:
 * - Floating glass nav bar with blur
 * - Chat FAB on dashboard page only
 * - Safe area aware for iOS PWA
 */

import { useState, useCallback, useEffect, memo, Suspense, lazy, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Leaf, MessageCircle } from '@/lib/icons';
import { SidebarNav } from './SidebarNav';
import { NavBar, MenuButton, InboxButton } from './NavBar';
import { PageTransition } from './PageTransition';
import { InboxSheet } from '@/components/inbox';
import { useAuthStore } from '@/state/auth';
import type { ActionCategory, TimeOption, CaregiverRole, FeedingPreference, SleepSchedule, SharingLevel } from '@/lib/types';
import { useTodayStore } from '@/state/today';
import { RewardToast } from '@/components/ui/Toast';
import { MandatoryUpdateScreen } from '@/components/ui/MandatoryUpdateScreen';
import { OfflineIndicator } from '@/components/ui/OfflineIndicator';
import { PWAInstallBanner } from '@/components/ui/PWAInstallBanner';
import { useAppUpdateCheck, useVisualViewport } from '@/lib/hooks';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useServiceWorker } from '@/hooks/useServiceWorker';
import { triggerHaptic } from '@/lib/haptics';
import { attachKeyboardObserver } from '@/lib/keyboard';
import { getFabBottomStyle } from '@/lib/safeArea';

// Lazy load OnboardingFlow for new users
const OnboardingFlow = lazy(() => import('@/components/onboarding/OnboardingFlow').then(m => ({ default: m.OnboardingFlow })));

// Pages that should NOT show the header (full-screen experiences)
const FULL_SCREEN_ROUTES = ['/share'];

// Only show Chat FAB on the dashboard page
const SHOW_CHAT_FAB_ROUTES = ['/today'];

// Pages that handle their own scrolling (chat experiences)
const SELF_SCROLL_ROUTES = ['/chat', '/partner'];

export const AppShell = memo(function AppShell() {
  const { user, userDoc, updateUserSettings, isBootstrapReady } = useAuthStore();
  const { lastReward, clearReward } = useTodayStore();
  const { mandatoryUpdate, refresh } = useAppUpdateCheck();
  const swState = useServiceWorker();
  const { isKeyboardVisible } = useVisualViewport();

  // Handle refresh for mandatory updates
  const handleRefresh = useCallback(() => {
    if (swState.updateWaiting) {
      swState.applyUpdate();
    } else {
      refresh();
    }
  }, [swState, refresh]);
  const pwaInstallState = usePWAInstall();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Onboarding state
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);
  const onboardingSubmitRef = useRef(false);

  // Handle onboarding completion - centralized handler for all routes
  // Returns { success: boolean } so OnboardingFlow can handle retry
  const handleOnboardingComplete = useCallback(async (data: {
    displayName: string;
    babyName?: string;
    babyBirthday?: Date;
    babyGender?: 'boy' | 'girl' | 'prefer-not-to-say';
    babyAge?: string;
    sleepSchedule?: SleepSchedule;
    timeOption: TimeOption;
    focusAreas: ActionCategory[];
    caregiverRole?: CaregiverRole;
    feedingPreferences?: FeedingPreference[];
    sharingLevel?: SharingLevel;
  }): Promise<{ success: boolean }> => {
    // Double-submit protection (synchronous guard)
    if (onboardingSubmitRef.current) {
      return { success: false };
    }
    onboardingSubmitRef.current = true;
    setOnboardingSubmitting(true);
    setOnboardingError(null);

    try {
      // Build settings object, excluding undefined values (Firestore doesn't accept undefined)
      const settings: Parameters<typeof updateUserSettings>[0] = {
        displayName: data.displayName,
        defaultTimeOption: data.timeOption,
        focusAreas: data.focusAreas,
        onboardingCompleted: true,
      };

      // Baby profile fields
      if (data.babyName) settings.babyName = data.babyName;
      if (data.babyBirthday) settings.babyBirthday = data.babyBirthday;
      if (data.babyGender) settings.babyGender = data.babyGender;
      if (data.babyAge) settings.babyAge = data.babyAge;

      // Other optional fields
      if (data.sleepSchedule) settings.sleepSchedule = data.sleepSchedule;
      if (data.caregiverRole) settings.caregiverRole = data.caregiverRole;
      if (data.feedingPreferences?.length) settings.feedingPreferences = data.feedingPreferences;
      if (data.sharingLevel) settings.sharingLevel = data.sharingLevel;

      await updateUserSettings(settings);
      return { success: true };
    } catch (err) {
      // Log only error code, no PII
      if (import.meta.env.DEV) {
        console.error('[Onboarding] Save failed:', (err as Error).name || 'unknown');
      }
      setOnboardingError('We couldn\'t save your setup. Please try again.');
      return { success: false };
    } finally {
      onboardingSubmitRef.current = false;
      setOnboardingSubmitting(false);
    }
  }, [updateUserSettings]);

  // Check if user is in onboarding (dedicated full-screen experience)
  // Only evaluate after bootstrap is ready to prevent flicker
  // During loading: show loading state (not onboarding)
  // After loading: new users won't have userDoc yet, existing users check onboardingCompleted
  const bootstrapReady = isBootstrapReady();
  const isOnboarding = bootstrapReady && (!userDoc || !userDoc.onboardingCompleted);

  // Check if current route should hide the header
  const hideHeader = isOnboarding || FULL_SCREEN_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );

  // Check if current route handles its own scrolling
  const isSelfScrollRoute = SELF_SCROLL_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Show Chat FAB only on the dashboard page
  const showChatFAB = SHOW_CHAT_FAB_ROUTES.some(route =>
    location.pathname === route
  );

  // Hide FAB when keyboard is visible
  const hideFAB = isKeyboardVisible;

  const handleOpenSidebar = useCallback(() => {
    triggerHaptic();
    setSidebarOpen(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleOpenInbox = useCallback(() => {
    setInboxOpen(true);
  }, []);

  const handleCloseInbox = useCallback(() => {
    setInboxOpen(false);
  }, []);

  const handleOpenChat = useCallback(() => {
    triggerHaptic();
    navigate('/chat');
  }, [navigate]);

  // Track scroll position for header elevation effect
  const handleMainScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setHasScrolled(scrollTop > 8);
  }, []);

  // Attach keyboard observer for iOS safe-area and keyboard handling
  useEffect(() => {
    const cleanup = attachKeyboardObserver();
    return cleanup;
  }, []);

  // Block the entire app for mandatory updates
  if (mandatoryUpdate) {
    return (
      <MandatoryUpdateScreen
        onUpdate={handleRefresh}
        isUpdating={swState.isUpdating}
      />
    );
  }

  return (
    <div
      className="flex flex-col min-h-dvh h-full overflow-hidden bg-wellness-gradient"
    >
      {/* Offline indicator */}
      <OfflineIndicator />

      {/* Skip link for accessibility - sr-only pattern with focus visibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50
                   focus:px-4 focus:py-2 focus:bg-white focus:rounded-lg focus:shadow-lg
                   focus:text-primary-600 focus:font-medium focus:outline-none focus:ring-2 focus:ring-primary-300"
      >
        Skip to main content
      </a>

      {/* Premium Header - Apple-inspired floating glass nav bar */}
      {!hideHeader && (
        <NavBar
          variant="brand"
          hasScrolled={hasScrolled}
          rightContent={
            <div className="flex items-center gap-0.5">
              <InboxButton onClick={handleOpenInbox} />
              <MenuButton onClick={handleOpenSidebar} isOpen={sidebarOpen} />
            </div>
          }
        />
      )}

      {/* Main content area - single vertical scroller (unless route handles its own scroll) */}
      <main
        id="main-content"
        className={`flex-1 min-h-0 max-w-lg mx-auto w-full overflow-x-hidden ${
          isSelfScrollRoute
            ? 'overflow-hidden flex flex-col'
            : 'overflow-y-auto momentum-scroll'
        }`}
        role="main"
        onScroll={isSelfScrollRoute ? undefined : handleMainScroll}
      >
        {/* Centralized onboarding gate - ensures ALL routes require onboarding */}
        {/* Show loading while bootstrap is in progress to prevent flicker */}
        {!bootstrapReady ? (
          <div className="h-full flex items-center justify-center">
            <Leaf className="w-10 h-10 text-primary-500 motion-safe:animate-pulse" />
          </div>
        ) : isOnboarding ? (
          <Suspense fallback={
            <div className="h-full flex items-center justify-center">
              <Leaf className="w-10 h-10 text-primary-500 motion-safe:animate-pulse" />
            </div>
          }>
            <OnboardingFlow
              initialName={userDoc?.displayName || user?.displayName || 'Friend'}
              onComplete={handleOnboardingComplete}
              isSubmitting={onboardingSubmitting}
              error={onboardingError}
              onClearError={() => setOnboardingError(null)}
            />
          </Suspense>
        ) : (
          <PageTransition className={isSelfScrollRoute ? 'flex-1 flex flex-col min-h-0' : 'h-full'}>
            <Outlet />
          </PageTransition>
        )}
      </main>

      {/* Sidebar Navigation */}
      <SidebarNav isOpen={sidebarOpen} onClose={handleCloseSidebar} triggerRef={menuButtonRef} />

      {/* Floating Chat Button - Dashboard only, hidden when keyboard visible */}
      {!isOnboarding && showChatFAB && !hideFAB && (
        <button
          onClick={handleOpenChat}
          className="fixed right-5 z-40 w-14 h-14 flex items-center justify-center touch-manipulation
                     motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2
                     hover:-translate-y-0.5 active:scale-[0.96] active:brightness-[0.98]"
          style={{
            ...getFabBottomStyle(),
            borderRadius: '50%',
            background: 'linear-gradient(145deg, #3B82F6 0%, #2563EB 100%)',
            boxShadow: '0 2px 8px rgba(59,130,246,0.35), 0 4px 16px rgba(59,130,246,0.2)',
          }}
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6 text-white" strokeWidth={2} />
        </button>
      )}

      {/* Reward toast */}
      {lastReward && (
        <RewardToast
          credits={lastReward.creditsAwarded}
          message={lastReward.rewardMessage}
          onClose={clearReward}
        />
      )}

      {/* PWA Install Banner - respects: installed state, 7d dismissal, 24h cooldown */}
      {!isOnboarding && pwaInstallState.shouldShowInstall && (
        <PWAInstallBanner installState={pwaInstallState} />
      )}

      {/* Inbox Sheet - Global messaging center */}
      <InboxSheet isOpen={inboxOpen} onClose={handleCloseInbox} />
    </div>
  );
});

